import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as request from 'request';
import Navbar from 'components/Navbar';
import { FirebaseContext } from 'contexts/Firebase';
import { PageLoader, showToastAlert } from 'Services/Utility';


const PaymentSettings = (props) => {
  const { firebase } = useContext(FirebaseContext);
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState('stripe');
  const [saving, setSaving] = useState(false);

  const callAdminApi = async (path, payload = {}) => {
    const idToken = await firebase.auth.currentUser.getIdToken(true);

    return new Promise((resolve, reject) => {
      const options = {
        method: 'POST',
        url: `${process.env.REACT_APP_API_URL}/admin/${path}`,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          idToken,
          ...payload
        })
      };

      return request(options, (error, response, body) => {
        if (error) {
          return reject(error);
        }

        let parsedBody = body;
        if (typeof body === 'string' && body !== '') {
          try {
            parsedBody = JSON.parse(body);
          } catch (parseError) {
            return reject(parseError);
          }
        }

        if (response.statusCode !== 200 || !parsedBody || parsedBody.status !== 'success') {
          return reject(new Error(parsedBody && parsedBody.message ? parsedBody.message : 'Unable to update payment settings'));
        }

        return resolve(parsedBody.data || {});
      });
    });
  };

  useEffect(() => {
    document.body.classList.add('dash-bg');
  }, []);

  useEffect(() => {
    firebase.auth.onAuthStateChanged(async (user) => {
      try {
        if (!user) {
          history.push('/login');
          return;
        }

        const data = await callAdminApi('getPaymentProvider');
        setProvider(data.provider === 'paypal' ? 'paypal' : 'stripe');
      } catch (error) {
        showToastAlert('Error', error.message || 'Unable to load payment settings');
      } finally {
        setLoading(false);
      }
    });
  }, [firebase.auth, history]);

  const saveProvider = async (nextProvider) => {
    const previousProvider = provider;
    try {
      setSaving(true);
      setProvider(nextProvider);
      const data = await callAdminApi('updatePaymentProvider', { provider: nextProvider });
      setProvider(data.provider === 'paypal' ? 'paypal' : 'stripe');
      showToastAlert('Success', `Active checkout gateway set to ${nextProvider}`);
    } catch (error) {
      setProvider(previousProvider);
      showToastAlert('Error', error.message || 'Unable to update payment gateway');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {loading && <PageLoader />}
      <div id="page-content-wrapper" className="extra-p">
        <Navbar toggleSideMenu={props.toggleSideMenu} />

        <div className="container mb-2">
          <div className="row">
            <div className="col-12 dash-head my-0">
              <h3 className="my-0">Payment Settings</h3>
            </div>
            <div className="col-12 mt-4">
              <div className="dash-box p-4">
                <h4 style={{ color: 'var(--main-blue)' }}>Active Checkout Gateway</h4>
                <p className="mb-4">Switch the site between Stripe and PayPal for one-time checkout. Recurring subscription checkouts continue to use Stripe.</p>

                <div className="d-flex" style={{ gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    className={`icon-btn add ${provider === 'stripe' ? 'active' : ''}`}
                    disabled={saving || provider === 'stripe'}
                    onClick={() => saveProvider('stripe')}
                    style={{ minWidth: '160px', padding: '12px 18px' }}
                  >
                    Use Stripe
                  </button>
                  <button
                    className={`icon-btn add ${provider === 'paypal' ? 'active' : ''}`}
                    disabled={saving || provider === 'paypal'}
                    onClick={() => saveProvider('paypal')}
                    style={{ minWidth: '160px', padding: '12px 18px' }}
                  >
                    Use PayPal
                  </button>
                </div>

                <div className="mt-4">
                  <strong>Current provider:</strong> {provider}
                </div>
                <div className="mt-2 text-muted">
                  Configure the PayPal webhook in your PayPal app to point at the deployed cloud function endpoint for `paypalWebhook`.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentSettings;